import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AppLayout, GlassCard, LoadingSpinner } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import EducationInvestment, { RoiBadge } from '../components/EducationInvestment';
import { GraduationCap, TrendingUp, AlertTriangle, Info, ArrowRight, Star, Scale, Sparkles, ShieldCheck, Zap, BarChart3, MapPin, Users, Building, Trophy, DollarSign, Filter, X, ChevronDown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import celebrate from '../utils/celebrate';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = (v) => (v || v === 0 ? `$${Number(v).toLocaleString()}` : '-');
const pct   = (v) => (v || v === 0 ? `${v}%` : '-');

const Stat = ({ label, value, tone = 'text-white', hint }) => (value === '-' || value === null || value === undefined) ? null : (
  <div className="rounded-xl border border-white/10 bg-white/[0.04] p-4">
    <p className="text-[11px] uppercase tracking-wider text-slate-300 font-semibold">{label}</p>
    <p className={`font-outfit text-xl font-semibold mt-1.5 ${tone}`}>{value}</p>
    {hint && <p className="text-[11px] text-slate-300/80 mt-1">{hint}</p>}
  </div>
);

const COLLECTIONS = {
  nc:      { label: 'North Carolina', names: ["University of North Carolina at Chapel Hill", "North Carolina State University at Raleigh", "Duke University", "Wake Forest University", "North Carolina A & T State University", "East Carolina University", "University of North Carolina at Charlotte", "Appalachian State University", "Elon University", "Davidson College"] },
  value:   { label: 'Best value',     names: ["University of Florida", "University of North Carolina at Chapel Hill", "Brigham Young University", "City College of New York", "University of Washington-Seattle Campus", "Georgia Institute of Technology-Main Campus", "University of California-Berkeley", "Purdue University-Main Campus"] },
  popular: { label: 'Most searched', names: ["Rutgers University-New Brunswick", "Pennsylvania State University-Main Campus", "Ohio State University-Main Campus", "University of Michigan-Ann Arbor", "University of Texas at Austin", "University of Florida", "Arizona State University Campus Immersion", "New York University"] },
  ivy:     { label: 'Ivy League',     names: ["Harvard University", "Yale University", "Princeton University", "Columbia University in the City of New York", "University of Pennsylvania", "Brown University", "Dartmouth College", "Cornell University"] },
  tech:    { label: 'Engineering & tech', names: ["Massachusetts Institute of Technology", "Georgia Institute of Technology-Main Campus", "California Institute of Technology", "Carnegie Mellon University", "Virginia Polytechnic Institute and State University", "Purdue University-Main Campus", "Rensselaer Polytechnic Institute"] },
  hbcu:    { label: 'HBCUs',          names: ["Howard University", "Spelman College", "Morehouse College", "Hampton University", "Florida A&M University", "North Carolina A & T State University", "Tuskegee University", "Xavier University of Louisiana", "Prairie View A & M University", "Jackson State University"] },
  community: { label: 'Community colleges', names: ["Santa Monica College", "Valencia College", "Miami Dade College", "Northern Virginia Community College", "Austin Community College District", "De Anza College"] },
};

const STATES = ['NC','SC','VA','GA','TN','FL','NY','NJ','PA','CA','TX','MA','MD','IL','OH','MI','WA','AZ','LA','MS','AL'];

const COLLECTION_HUE = {
  nc: '#22d3ee', value: '#a78bfa', popular: '#38bdf8',
  ivy: '#c084fc', tech: '#f472b6', hbcu: '#fbbf24', community: '#67e8f9',
};

const PROB_COLORS = {
  likely:    'text-emerald-400 bg-emerald-500/10 border-emerald-500/30',
  possible:  'text-amber-400 bg-amber-500/10 border-amber-500/30',
  reach:     'text-orange-400 bg-orange-500/10 border-orange-500/30',
  far_reach: 'text-red-400 bg-red-500/10 border-red-500/30',
  unknown:   'text-slate-300 bg-slate-700/30 border-slate-600/30',
};

// ── Ring meter ────────────────────────────────────────────────────────────────
function Ring({ score, color, size = 76, sw = 8 }) {
  const r        = (size - sw) / 2;
  const circ     = 2 * Math.PI * r;
  const hasScore = typeof score === 'number';
  const pctVal   = hasScore ? Math.min(100, Math.max(0, score)) : 0;
  const ringColor = hasScore ? color : 'rgba(100,116,139,0.5)';
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink: 0 }}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={sw} />
      <circle cx={size/2} cy={size/2} r={r} fill="none"
        stroke={ringColor} strokeWidth={sw}
        strokeLinecap="round"
        strokeDasharray={hasScore ? String(circ) : `${circ * 0.12} ${circ * 0.04} ${circ * 0.12} ${circ * 0.04} ${circ}`}
        strokeDashoffset={hasScore ? String(circ * (1 - pctVal / 100)) : '0'}
        transform={`rotate(-90 ${size/2} ${size/2})`}
      />
      <text x="50%" y="44%" dominantBaseline="middle" textAnchor="middle"
        style={{ fontSize: `${Math.round(size * 0.22)}px`, fontWeight: '800', fill: hasScore ? '#ffffff' : '#94a3b8', fontFamily: 'inherit' }}>
        {hasScore ? score : '?'}
      </text>
      {hasScore && (
        <text x="50%" y="67%" dominantBaseline="middle" textAnchor="middle"
          style={{ fontSize: `${Math.round(size * 0.13)}px`, fontWeight: '700', fill: 'rgba(148,163,184,0.85)', fontFamily: 'inherit', letterSpacing: '0.05em' }}>
          %
        </text>
      )}
    </svg>
  );
}

// ── School Logo, initials badge with cyan glow, no external API ──────────────
function SchoolLogo({ sc, h = 48, w = 48, rounded = 'rounded-xl', className = '' }) {
  const initials = sc.name
    .split(/\s+/)
    .filter(word => /^[A-Z]/.test(word))
    .slice(0, 2)
    .map(word => word[0])
    .join('') || sc.name.slice(0, 2).toUpperCase();

  return (
    <div
      style={{
        height: h, width: w, minWidth: w,
        fontSize: Math.round(h * 0.32),
        background: 'linear-gradient(135deg, #0f172a, #1e293b)',
        boxShadow: '0 0 18px rgba(34,211,238,0.55), 0 0 4px rgba(34,211,238,0.3)',
        border: '1px solid rgba(34,211,238,0.35)',
        color: '#22d3ee',
      }}
      className={`${rounded} shrink-0 flex items-center justify-center font-black tracking-tight ${className}`}
    >
      {initials}
    </div>
  );
}

// ── Fit Score Engine (for 6-component breakdown view) ─────────────────────────
function computeFitScores(s, user, costSummary) {
  const sat          = user?.sat_score || 0;
  const userState    = (user?.state || '').trim().toUpperCase();
  const career       = user?.career_aspirations || user?.target_career || '';
  const incomeFloor  = costSummary?.required_gross || 0;
  const earnings10   = s.earnings_10yr || s.earnings_6yr || 0;
  const gradRate     = s.graduation_rate || 50;
  const retentionRate = s.retention_rate || 70;
  const collegeSAT   = s.sat_average || 1100;
  const schoolState  = (s.state || '').trim().toUpperCase();
  const NC_ALIASES   = ['NC', 'NORTH CAROLINA'];

  let careerScore = null;
  const careerHints = [];
  if (earnings10 > 0) {
    const baseline = incomeFloor > 0 ? incomeFloor : 60000;
    careerScore = Math.min(100, Math.max(20, Math.round((earnings10 / baseline) * 75)));
    if (!incomeFloor) careerHints.push({ type: 'improve', text: 'Set your income target for a personalized score', link: '/app/calculator' });
    else careerHints.push({ type: 'info', text: `Graduates earn $${Math.round(earnings10/1000)}K vs your $${Math.round(incomeFloor/1000)}K income target` });
  } else {
    careerHints.push({ type: 'warn', text: 'Earnings data not reported by this school' });
  }

  let academicScore = null;
  const academicHints = [];
  if (!sat) {
    academicHints.push({ type: 'improve', text: 'Add your SAT or ACT score to unlock Academic Fit', link: '/app/portfolio' });
  } else {
    const delta = sat - collegeSAT;
    academicScore = delta >= 200 ? 95 : delta >= 100 ? 88 : delta >= 0 ? 78 : delta >= -50 ? 68 : delta >= -100 ? 55 : 42;
    academicHints.push({ type: 'info', text: `Your SAT ${sat} vs school avg ${collegeSAT} (${delta >= 0 ? '+' : ''}${delta} pts)` });
  }

  const typeBonus = s.ownership === 'Private nonprofit' ? 22 : s.ownership === 'Public' ? 18 : 12;
  const prestigeScore = Math.min(100, Math.max(25, Math.round(gradRate * 0.48 + retentionRate * 0.27 + typeBonus)));
  const prestigeHints = [{ type: 'info', text: `${gradRate}% graduation rate · ${retentionRate}% first-year retention` }];

  let locationScore = 65;
  const locationHints = [];
  if (userState && schoolState) {
    const inState = userState === schoolState || (NC_ALIASES.includes(userState) && NC_ALIASES.includes(schoolState));
    locationScore = inState ? 95 : 52;
    locationHints.push(inState
      ? { type: 'good', text: `${s.city} is in your home state, in-state tuition applies` }
      : { type: 'info', text: 'Out of state, out-of-state tuition rate applies' });
  } else {
    locationHints.push({ type: 'improve', text: 'Set your state on your profile to personalize Location Fit', link: '/app/profile' });
  }

  const campusScore = s.size > 30000 ? 88 : s.size > 15000 ? 82 : s.size > 5000 ? 75 : s.size > 0 ? 68 : 65;
  const campusHints = [{ type: 'info', text: s.size > 0 ? `${s.size?.toLocaleString()} enrolled · ${s.size > 20000 ? 'Large research university' : s.size > 8000 ? 'Mid-size campus' : 'Smaller campus'}` : 'Campus size not reported' }];

  const wlbScore = Math.min(100, Math.max(30, Math.round(gradRate * 0.5 + retentionRate * 0.3 + 15)));
  const wlbHints = [{ type: 'info', text: `${gradRate}% graduation rate reflects academic support quality` }];

  const COMPONENTS = [
    { key: 'career',   label: 'Career Outcomes',      weight: 25, score: careerScore,   hints: careerHints,   color: '#22d3ee' },
    { key: 'academic', label: 'Academic Fit',          weight: 20, score: academicScore, hints: academicHints, color: '#818cf8' },
    { key: 'prestige', label: 'Prestige / Reputation', weight: 20, score: prestigeScore, hints: prestigeHints, color: '#a78bfa' },
    { key: 'location', label: 'Location Fit',          weight: 15, score: locationScore, hints: locationHints, color: '#34d399' },
    { key: 'campus',   label: 'Campus & Environment',  weight: 10, score: campusScore,   hints: campusHints,   color: '#fb923c' },
    { key: 'wlb',      label: 'Work-Life Balance',     weight: 10, score: wlbScore,      hints: wlbHints,      color: '#38bdf8' },
  ];

  const scored      = COMPONENTS.filter(c => c.score !== null);
  const totalW      = scored.reduce((a, c) => a + c.weight, 0);
  const weightedSum = scored.reduce((a, c) => a + c.score * c.weight, 0);
  const overall     = totalW >= 40 ? Math.round(weightedSum / totalW) : null;

  const reasons = [];
  if (careerScore && careerScore >= 70 && earnings10 > 0) reasons.push(`Graduates earn $${Math.round(earnings10/1000)}K median 10 yrs after enrollment${career ? `, strong outcomes for ${career}` : ''}`);
  if (locationScore >= 90)                  reasons.push(`${s.city} is in your home state, in-state tuition applies`);
  if (academicScore && academicScore >= 80) reasons.push(`Your SAT score puts you in the competitive range for admission`);
  if (prestigeScore >= 78)                  reasons.push(`${gradRate}% graduation rate, well above the national average`);
  if (s.avg_net_price && s.avg_net_price < 20000) reasons.push(`Average net price $${Number(s.avg_net_price).toLocaleString()} after grants and scholarships`);
  if (s.acceptance_rate > 50)               reasons.push(`${s.acceptance_rate}% acceptance rate, a more accessible school`);
  if (career && earnings10 > 55000)         reasons.push(`Excellent career placement track record for ${career} and related fields`);
  while (reasons.length < 3)               reasons.push('Complete your profile for more personalized insights');

  return { overall, components: COMPONENTS, reasons: reasons.slice(0, 4) };
}

// ── Multi-Segment Donut Chart ─────────────────────────────────────────────────
function DonutChart({ score, components }) {
  const SZ  = 130;
  const SW  = 20;
  const R   = (SZ - SW) / 2;
  const C   = 2 * Math.PI * R;
  const GAP = 2.5;
  let acc = 0;
  return (
    <svg width={SZ} height={SZ} viewBox={`0 0 ${SZ} ${SZ}`} style={{ flexShrink: 0 }}>
      <circle cx={SZ/2} cy={SZ/2} r={R} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={SW} />
      {components.map(c => {
        const start  = acc;
        acc += c.weight;
        const segLen = Math.max(0, C * (c.weight / 100) - GAP);
        return (
          <circle key={c.key} cx={SZ/2} cy={SZ/2} r={R} fill="none"
            stroke={c.color} strokeWidth={SW} strokeLinecap="butt"
            strokeDasharray={`${segLen} ${C - segLen}`}
            strokeDashoffset={C * (1 - start / 100)}
            transform={`rotate(-90 ${SZ/2} ${SZ/2})`}
            opacity={0.9}
          />
        );
      })}
      <text x="50%" y="46%" dominantBaseline="middle" textAnchor="middle"
        style={{ fontSize: '26px', fontWeight: '800', fill: '#ffffff', fontFamily: 'inherit' }}>
        {typeof score === 'number' ? score : '-'}
      </text>
      <text x="50%" y="65%" dominantBaseline="middle" textAnchor="middle"
        style={{ fontSize: '7.5px', fill: '#C6D4E8', letterSpacing: '0.1em' }}>
        FP FIT SCORE
      </text>
    </svg>
  );
}

// ── FitBreakdownView, drill-down opened by clicking FP Fit ring ──────────────
function FitBreakdownView({ college: s, user, costSummary, fpFit, onBack }) {
  const [fitView,     setFitView]     = useState('breakdown');
  const [expandedRow, setExpandedRow] = useState(null);

  const { overall, components, reasons } = computeFitScores(s, user, costSummary);
  const sat        = user?.sat_score || 0;
  const career     = user?.career_aspirations || user?.target_career || '';
  const collegeSAT = s.sat_average || 1100;
  const satDelta   = sat - collegeSAT;
  const earnings10 = s.earnings_10yr || s.earnings_6yr || 0;

  const displayScore = overall ?? fpFit;
  const ringColor    = !displayScore ? '#C6D4E8' : displayScore >= 85 ? '#22d3ee' : displayScore >= 70 ? '#818cf8' : displayScore >= 55 ? '#fb923c' : '#ef4444';
  const matchLabel   = !displayScore ? 'Incomplete' : displayScore >= 85 ? 'Strong Match' : displayScore >= 70 ? 'Good Match' : displayScore >= 55 ? 'Possible Fit' : 'Reach School';
  const matchDesc    = !displayScore
    ? 'Complete your profile to unlock personalized fit scores.'
    : displayScore >= 85
    ? 'Highly aligned with your preferences, goals, and what matters most in your college experience.'
    : displayScore >= 70
    ? 'A solid match for your goals. Review the fit factors below before finalizing your list.'
    : 'This school has matching areas but may require careful planning.';

  const perfText = (c) => ({
    career:   earnings10 > 0 ? `$${Math.round(earnings10/1000)}K median earnings 10 yrs after enrollment${career ? `, strong for ${career}` : ''}` : 'Earnings data not reported for this school',
    academic: sat > 0 ? `Your SAT ${sat} vs school avg ${collegeSAT} (${satDelta >= 0 ? '+' : ''}${satDelta} pts)` : 'Add SAT or ACT score to unlock',
    prestige: `${s.graduation_rate || '-'}% graduation · ${s.retention_rate || '-'}% first-year retention`,
    location: s.city ? `${s.city}, ${s.state}, ${(user?.state || '').toUpperCase() === (s.state || '').toUpperCase() ? 'in-state' : 'out-of-state'}` : '-',
    campus:   s.size > 0 ? `${s.size?.toLocaleString()} students enrolled` : 'Enrollment not reported',
    wlb:      `${s.graduation_rate || '-'}% graduation rate reflects student success`,
  }[c.key] || c.label);

  return (
    <div className="mt-4" data-testid="fit-breakdown-view">
      {/* Back header */}
      <div className="flex items-center gap-3 mb-4">
        <button onClick={onBack}
          className="flex items-center gap-1.5 px-4 py-2 rounded-xl border border-white/15 text-[13px] font-medium text-slate-300 hover:text-white hover:border-slate-500 transition-colors">
          ← Back to {s.name.split(' ').slice(0,3).join(' ')}
        </button>
        <div className="text-[12px] text-slate-300">
          College Explorer / <span className="text-slate-300">{s.name}</span> / <span className="text-cyan-400">FuturePath Fit Breakdown</span>
        </div>
      </div>

      {/* FuturePath Fit Hero */}
      <div className="glass-tint glass-cyan rounded-2xl mb-4 relative overflow-hidden"
        style={{ boxShadow: '0 0 80px -25px rgba(34,211,238,0.35)' }}>
        <div className="absolute right-0 top-0 w-80 h-80 opacity-10 pointer-events-none"
          style={{ background: 'radial-gradient(circle at 80% 20%, #22d3ee, transparent 60%)' }} />
        <div className="relative px-8 py-7 flex items-center gap-8 flex-wrap">
          <Ring score={displayScore} color={ringColor} size={140} sw={13} />
          <div className="flex-1 min-w-[220px]">
            <p className="text-[10.5px] font-bold uppercase tracking-[0.22em] mb-2 text-cyan-400">FuturePath Fit</p>
            <h3 className="text-4xl font-black text-white mb-2 leading-none tracking-tight">{matchLabel}</h3>
            <p className="text-slate-300/75 text-[14px] leading-relaxed max-w-lg">{matchDesc}</p>
          </div>
        </div>
      </div>

      {/* 2-col breakdown */}
      <div className="grid lg:grid-cols-5 gap-4 mb-4">
        {/* Left: Breakdown table */}
        <div className="lg:col-span-3 glass-tint rounded-2xl px-6 py-5">
          <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
            <div>
              <h3 className="text-[15px] font-bold text-white">How This College Aligns With You</h3>
              <p className="text-[12px] text-slate-300 mt-0.5">6 weighted factors drawn from your profile and school data.</p>
            </div>
            <div className="flex rounded-lg border border-white/10 overflow-hidden">
              {[['breakdown', 'Fit Breakdown'], ['what_it_means', 'What It Means']].map(([v, label]) => (
                <button key={v} onClick={() => setFitView(v)}
                  className={`px-3 py-1.5 text-[12px] font-medium transition-colors ${fitView === v ? 'bg-white/10 text-white' : 'text-slate-300 hover:text-white'}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          {fitView === 'breakdown' ? (
            <>
              <div className="px-4 mb-2 text-[10px] uppercase tracking-wider text-slate-300 font-semibold hidden md:grid"
                style={{ gridTemplateColumns: '1fr 170px 52px', gap: '14px' }}>
                <span>What Matters to You</span>
                <span>Performance</span>
                <span className="text-center">Your Fit</span>
              </div>
              <div className="space-y-0.5">
                {components.map(c => {
                  const open = expandedRow === c.key;
                  return (
                    <div key={c.key} className="rounded-xl overflow-hidden">
                      <button
                        onClick={() => setExpandedRow(open ? null : c.key)}
                        className="w-full px-4 py-3 hover:bg-white/[0.04] transition-colors text-left"
                        style={{ display: 'grid', gridTemplateColumns: '1fr 170px 52px', gap: '14px', alignItems: 'center' }}>
                        <div>
                          <p className="text-[13px] font-semibold text-white">{c.label}</p>
                          <p className="text-[11px] text-slate-300 mt-0.5">Weight {c.weight}%</p>
                        </div>
                        <div>
                          {c.score !== null ? (
                            <>
                              <div className="h-[5px] rounded-full bg-white/[0.07] overflow-hidden mb-1.5">
                                <div className="h-full rounded-full"
                                  style={{ width: `${c.score}%`, background: `linear-gradient(90deg, ${c.color}77, ${c.color})`, transition: 'width 0.7s ease' }} />
                              </div>
                              <p className="text-[11px] text-slate-300 leading-snug"
                                style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                {perfText(c)}
                              </p>
                            </>
                          ) : (
                            <p className="text-[11px] text-amber-400/80 flex items-start gap-1">
                              <AlertTriangle className="h-3 w-3 shrink-0 mt-0.5" />{perfText(c)}
                            </p>
                          )}
                        </div>
                        <div className="flex justify-center">
                          {c.score !== null ? (
                            <div className="h-9 w-9 rounded-full border-2 flex flex-col items-center justify-center shrink-0"
                              style={{ borderColor: c.color, color: c.color, background: `${c.color}18` }}>
                              <span className="text-[11px] font-black leading-none">{c.score}</span>
                              <span className="text-[8px] font-bold leading-none opacity-70">%</span>
                            </div>
                          ) : (
                            <div className="h-9 w-9 rounded-full border-2 border-dashed border-slate-700 flex items-center justify-center shrink-0">
                              <span className="text-[11px] text-slate-300">-</span>
                            </div>
                          )}
                        </div>
                      </button>
                      {open && (
                        <div className="mx-4 mb-2 px-4 py-3 rounded-lg bg-white/[0.04] border border-white/[0.06] space-y-2">
                          {c.hints.map((h, hi) => (
                            <div key={hi} className={`flex items-start gap-2 text-[12px] leading-relaxed ${h.type === 'improve' ? 'text-amber-300/80' : h.type === 'good' ? 'text-emerald-400/80' : 'text-slate-300'}`}>
                              {h.type === 'improve' ? <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                               : h.type === 'good'    ? <ShieldCheck   className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                               :                        <Info           className="h-3.5 w-3.5 shrink-0 mt-0.5" />}
                              <span>{h.text}</span>
                              {h.link && <a href={h.link} className="ml-auto shrink-0 underline text-cyan-400 hover:no-underline">Add now →</a>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="space-y-4">
              <div className="rounded-xl border border-white/[0.07] bg-white/[0.03] px-5 py-4">
                <p className="text-[13px] font-bold text-white mb-1.5">What This Means</p>
                <p className="text-[13px] text-slate-300 leading-relaxed">{matchDesc}</p>
              </div>
              {components.filter(c => c.score !== null).map(c => (
                <div key={c.key} className="flex items-start gap-3.5">
                  <div className="h-8 w-8 rounded-full border-2 flex flex-col items-center justify-center shrink-0 mt-0.5"
                    style={{ borderColor: c.color, color: c.color, background: `${c.color}18` }}>
                    <span className="text-[10px] font-black leading-none">{c.score}</span>
                    <span className="text-[7px] font-bold leading-none opacity-70">%</span>
                  </div>
                  <div>
                    <p className="text-[13px] font-semibold text-white">{c.label}</p>
                    <p className="text-[12px] text-slate-300 mt-0.5">{c.hints[0]?.text}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right: Top Reasons + Donut */}
        <div className="lg:col-span-2 flex flex-col gap-4">
          <div className="glass-tint rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="h-4 w-4 shrink-0 text-cyan-400" />
              <h4 className="text-[13px] font-bold text-white">Top Reasons This Is a {matchLabel}</h4>
            </div>
            <ul className="space-y-3.5">
              {reasons.map((r, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5 text-cyan-400" />
                  <p className="text-[12.5px] text-slate-300 leading-relaxed">{r}</p>
                </li>
              ))}
            </ul>
          </div>

          <div className="glass-tint rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <Zap className="h-4 w-4 text-slate-300 shrink-0" />
              <h4 className="text-[13px] font-bold text-white">How Your Preferences Impact This Score</h4>
            </div>
            <div className="flex items-center gap-4">
              <DonutChart score={displayScore} components={components} />
              <div className="space-y-2 flex-1 min-w-0">
                {components.map(c => (
                  <div key={c.key} className="flex items-center gap-2 min-w-0">
                    <div className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: c.color }} />
                    <span className="text-[11.5px] text-slate-300 shrink-0 tabular-nums">{c.weight}%</span>
                    <span className="text-[11.5px] text-slate-300 truncate">{c.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── College Detail View, main view (4 rings) ─────────────────────────────────
function CollegeDetailView({ college: s, user, costSummary, isSaved, onSave, onBack, onCompare }) {
  const [tab,          setTab]          = useState('overview');
  const [showBreakdown, setShowBreakdown] = useState(false);

  // Profile data
  const sat       = user?.sat_score || 0;
  const career    = user?.career_aspirations || user?.target_career || '';
  const postSec   = user?.postsecondary_intent || '';

  // School metadata
  const collegeSAT = s.sat_average || 1100;
  const satDelta   = sat - collegeSAT;
  const levelLabel = s.highest_degree === 4 ? '4-Year' : s.highest_degree === 2 ? '2-Year' : '4-Year';
  const typeLabel  = s.ownership === 'Public' ? 'Public' : s.ownership === 'Private nonprofit' ? 'Private' : s.ownership || 'Private';
  const earnings10 = s.earnings_10yr || s.earnings_6yr || 0;
  const netPrice   = s.avg_net_price || s.tuition_in_state || 30000;
  const incomeFloor = costSummary?.required_gross || 0;
  const debt       = s.median_debt || 20000;

  // ── 4 Fit Scores ──────────────────────────────────────────────────────────

  // 1. Admissions Fit, SAT vs school average (null if no SAT)
  const admitNum = !sat ? null : satDelta >= 200 ? 95 : satDelta >= 100 ? 88 : satDelta >= 0 ? 78 : satDelta >= -50 ? 68 : satDelta >= -100 ? 55 : 42;
  const admitLabel = !sat ? 'Add SAT →' : admitNum >= 80 ? 'LIKELY' : admitNum >= 65 ? 'TARGET' : admitNum >= 50 ? 'POSSIBLE' : 'REACH';
  const admitColor = !sat ? '#C6D4E8' : '#818cf8';

  // 2. Financial Fit, net price vs income floor or $65K baseline
  const baseline     = incomeFloor > 0 ? incomeFloor : 65000;
  const financialFit = Math.min(98, Math.max(15, Math.round(100 - (netPrice / baseline) * 100)));
  const financialLabel = financialFit >= 70 ? 'Manageable' : financialFit >= 45 ? 'Plan Ahead' : 'Review Aid';

  // 3. Career ROI, earnings vs national median ($55K)
  const careerROI = !earnings10 ? null : Math.min(98, Math.max(15, Math.round((earnings10 / 55000) * 70)));
  const careerLabel = !earnings10 ? 'No data' : careerROI >= 70 ? 'Strong' : careerROI >= 45 ? 'Moderate' : 'Low';
  const careerColor = !earnings10 ? '#C6D4E8' : '#fb923c';

  // 4. FuturePath Fit, weighted composite
  const a = admitNum   ?? financialFit;   // use financial as fallback if no SAT
  const c = careerROI  ?? financialFit;   // use financial as fallback if no earnings
  const fpFit = Math.min(98, Math.round(a * 0.35 + financialFit * 0.35 + c * 0.30));
  const fpLabel = fpFit >= 85 ? 'Strong Match' : fpFit >= 70 ? 'Good Match' : fpFit >= 55 ? 'Possible Fit' : 'Reach School';
  const fpColor = fpFit >= 85 ? '#22d3ee' : fpFit >= 70 ? '#818cf8' : fpFit >= 55 ? '#fb923c' : '#ef4444';

  // Missing data
  const missing = [];
  if (!sat)        missing.push({ text: 'SAT / ACT score, improves Admissions Fit accuracy',   link: '/app/portfolio' });
  if (!career)     missing.push({ text: 'Career goal, personalizes Career ROI calculation',     link: '/app/explore' });
  if (!incomeFloor) missing.push({ text: 'Income target, improves Financial Fit accuracy',      link: '/app/calculator' });

  const TABS = ['Overview', 'Academics', 'Admissions', 'Cost & Aid', 'Career Outcomes', 'Campus Life', 'Athletics'];
  const tabKey = t => t.toLowerCase().replace(/\s+&\s+/, '_').replace(/\s+/g, '_');

  const shortName = s.name.length > 26 ? s.name.split(/[-,:]/)[0].trim() : s.name;
  const intendedMajor = user?.intended_major || career || null;
  const campusSize = !s.size ? '-' : s.size >= 20000 ? 'Large' : s.size >= 5000 ? 'Medium' : 'Small';
  const setting = typeLabel === 'Public' ? 'Urban / Research' : 'Campus';

  // "Why this college fits you", derived from profile + school data
  const fitReasons = [
    intendedMajor
      ? { icon: <GraduationCap className="h-4 w-4" />, text: `Offers pathways aligned with ${intendedMajor}` }
      : { icon: <GraduationCap className="h-4 w-4" />, text: 'Add an intended major to personalize this analysis' },
    { icon: <MapPin className="h-4 w-4" />, text: `${s.city}, ${s.state}, ${typeLabel} ${levelLabel} institution` },
    earnings10
      ? { icon: <TrendingUp className="h-4 w-4" />, text: `Graduates earn a median of ${money(earnings10)} 10 years after entry` }
      : { icon: <TrendingUp className="h-4 w-4" />, text: 'Earnings data not published for this institution' },
    { icon: <Sparkles className="h-4 w-4" />, text: financialFit >= 60 ? `Net price of ${money(netPrice)} is manageable against your income target` : `Net price of ${money(netPrice)} will need an aid plan` },
  ];

  const snapshotRows = [
    { icon: <GraduationCap className="h-4 w-4 text-cyan-300" />, label: 'Intended Major',      value: intendedMajor || 'Not set' },
    { icon: <Sparkles      className="h-4 w-4 text-emerald-300" />, label: 'Estimated Net Price', value: `${money(netPrice)} / year` },
    { icon: <Users         className="h-4 w-4 text-violet-300" />, label: 'Campus Size',        value: campusSize },
    { icon: <Building      className="h-4 w-4 text-sky-300" />,    label: 'Setting',            value: setting },
    { icon: <Trophy        className="h-4 w-4 text-amber-300" />,  label: 'Athletics',          value: typeLabel === 'Public' ? 'NCAA Division I' : 'NCAA Division II / III' },
  ];

  const scoreCards = [
    {
      key: 'futurepath', title: 'FuturePath Fit', ring: fpFit, pct: fpFit, color: fpColor,
      label: fpLabel, desc: 'Fits your goals and college preferences',
      onClick: () => setShowBreakdown(true), testid: 'fp-fit-ring-btn',
    },
    {
      key: 'admissions', title: 'Admissions Fit',
      ring: admitNum ?? 0, pct: admitNum, big: sat ? null : admitLabel, color: '#a78bfa',
      label: admitLabel, desc: !sat ? 'Add your test score for an accurate read' : admitNum >= 78 ? 'Your academic profile is competitive' : 'Your profile is below the middle band',
    },
    {
      key: 'financial', title: 'Financial Fit', ring: financialFit, pct: financialFit, color: '#22d3ee',
      label: financialLabel, desc: 'Affordability after estimated aid',
    },
    {
      key: 'career', title: 'Career ROI', ring: careerROI ?? 0, pct: careerROI, color: '#f472b6',
      label: careerLabel, desc: career ? `Outcomes for ${career}` : 'Median graduate earnings vs national',
    },
  ];

  // Show drill-down breakdown when FP Fit ring is clicked
  if (showBreakdown) {
    return (
      <FitBreakdownView
        college={s}
        user={user}
        costSummary={costSummary}
        fpFit={fpFit}
        onBack={() => setShowBreakdown(false)}
      />
    );
  }

  return (
    <div className="mt-4" data-testid="college-detail-view">

      {/* ── Header ── */}
      <div className="glass-tint glass-cyan rounded-2xl px-6 py-5 mb-4">
        <div className="flex items-center gap-2 mb-4 text-[12px]">
          <button onClick={onBack} className="font-medium text-cyan-400 hover:text-cyan-300 transition-colors">
            College Explorer
          </button>
          <span className="text-slate-300">/</span>
          <span className="text-slate-300 truncate max-w-sm">{s.name}</span>
        </div>
        <div className="flex items-start justify-between gap-5 flex-wrap">
          <div className="flex items-start gap-4 min-w-0">
            <SchoolLogo sc={s} h={64} w={64} />
            <div className="min-w-0">
              <h2 className="text-2xl font-bold text-white leading-tight">{s.name}</h2>
              <div className="flex items-center gap-1.5 mt-1.5 text-[13px] text-slate-300 flex-wrap">
                <MapPin className="h-3.5 w-3.5 shrink-0" />
                <span>{s.city}, {s.state}</span>
                <span className="text-slate-300">·</span><span>{typeLabel}</span>
                <span className="text-slate-300">·</span><span>{levelLabel}</span>
                {typeLabel === 'Public' && <><span className="text-slate-300">·</span><span>NCAA Division I</span></>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0 flex-wrap">
            <button onClick={onBack}
              className="flex items-center gap-1.5 rounded-lg border border-white/15 px-4 py-2 text-[13px] font-medium text-slate-300 hover:text-white transition-colors">
              ← Back
            </button>
            <button onClick={e => onCompare(s, e)}
              className="flex items-center gap-1.5 rounded-lg border border-white/15 px-4 py-2 text-[13px] font-medium text-slate-300 hover:text-white transition-colors">
              <BarChart3 className="h-4 w-4" /> Compare
            </button>
            <button onClick={e => onSave(s, e)}
              className="flex items-center gap-2 rounded-lg px-5 py-2 text-[13px] font-semibold text-white hover:opacity-90 transition-opacity"
              style={{ background: 'linear-gradient(135deg, #0EA5E9, #4F46E5)' }}>
              <Sparkles className="h-4 w-4" />Add to My Plan
            </button>
          </div>
        </div>
      </div>

      {/* ── 4 Score Cards, brand-consistent, aligned, percentage-first ── */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 mb-4" data-testid="fit-score-cards">
        {scoreCards.map(c => {
          const Wrapper = c.onClick ? 'button' : 'div';
          return (
            <Wrapper
              key={c.key}
              {...(c.onClick ? { onClick: c.onClick, 'data-testid': c.testid, type: 'button' } : {})}
              className={`rounded-2xl border p-5 text-left w-full flex flex-col ${c.onClick ? 'group cursor-pointer hover:border-cyan-300/50 transition-colors' : ''}`}
              style={{
                borderColor: `${c.color}33`,
                background: `${c.color}0a`,
                boxShadow: c.key === 'futurepath' ? `0 20px 60px -50px ${c.color}` : 'none',
              }}>
              <div className="flex items-baseline justify-between gap-2">
                <p className="text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color: c.color }}>
                  {c.title}
                </p>
                {c.pct != null && (
                  <p className="text-2xl font-bold leading-none" style={{ color: c.color }}
                    data-testid={`score-pct-${c.key}`}>
                    {c.pct}%
                  </p>
                )}
              </div>
              <div className="mt-4 flex items-center gap-4">
                {c.ring !== undefined && c.pct != null ? (
                  <Ring score={c.ring} color={c.color} size={64} sw={7} />
                ) : c.big ? (
                  <div className="w-[64px] shrink-0">
                    <p className="text-[20px] font-black leading-none" style={{ color: c.color }}>{c.big}</p>
                  </div>
                ) : null}
                <div className="min-w-0 flex-1">
                  <p className="text-[14px] font-bold text-white leading-tight">{c.label}</p>
                  <p className="text-[11.5px] text-slate-300 mt-1 leading-snug">{c.desc}</p>
                </div>
              </div>
              {c.onClick && (
                <p className="text-[10.5px] text-cyan-300/80 group-hover:text-cyan-200 transition-colors mt-4 flex items-center gap-1">
                  View full breakdown <ArrowRight className="h-3 w-3" />
                </p>
              )}
            </Wrapper>
          );
        })}
      </div>

      {/* ── Tab Bar ── */}
      <div className="flex border-b border-white/[0.08] overflow-x-auto">
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(tabKey(t))}
            className={`px-5 py-3.5 text-[13px] font-medium whitespace-nowrap transition-colors border-b-2 -mb-px ${
              tab === tabKey(t) ? 'border-cyan-400 text-white' : 'border-transparent text-slate-300 hover:text-white'}`}>
            {t}
          </button>
        ))}
      </div>

      {/* ── Tab Content ── */}
      <div className="glass-tint rounded-b-2xl p-6 mb-4">
        {tab === 'overview' && (
          <div className="grid gap-4 lg:grid-cols-3" data-testid="overview-columns">

            {/* Col 1, Why this school fits you */}
            <div>
              <p className="text-[13px] font-bold text-white mb-4">Why {shortName} fits you</p>
              <ul className="space-y-3">
                {fitReasons.map((r, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="mt-0.5 shrink-0 text-cyan-300">{r.icon}</span>
                    <span className="text-[12.5px] text-slate-300 leading-snug">{r.text}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => setShowBreakdown(true)}
                data-testid="see-all-fit-factors"
                className="mt-4 flex items-center gap-1.5 text-[12px] font-semibold text-cyan-400 hover:text-cyan-300 transition-colors">
                See all fit factors <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </div>

            {/* Col 2, College Snapshot */}
            <div className="lg:border-x lg:border-white/[0.08] lg:px-6">
              <p className="text-[13px] font-bold text-white mb-4">College Snapshot</p>
              <div className="space-y-1">
                {snapshotRows.map(row => (
                  <div key={row.label} className="flex items-center justify-between gap-3 py-2.5 border-b border-white/[0.06]">
                    <div className="flex items-center gap-2.5 text-[12px] text-slate-300">
                      <span className="shrink-0">{row.icon}</span>{row.label}
                    </div>
                    <span className="text-[12.5px] font-semibold text-white text-right">{row.value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Col 3, Improve your analysis */}
            <div>
              <p className="text-[13px] font-bold text-white mb-4">Improve your analysis</p>
              {missing.length > 0 ? (
                <ul className="space-y-3">
                  {missing.map((m, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <AlertTriangle className="h-4 w-4 text-amber-300 shrink-0 mt-0.5" />
                      <span className="text-[12.5px] text-slate-300 leading-snug">{m.text}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-[12.5px] text-slate-300">Your profile is complete, every fit score on this page is using your real data.</p>
              )}
              <a href="/app/portfolio" data-testid="complete-academic-profile"
                className="mt-4 inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-[12.5px] font-semibold text-white hover:opacity-90 transition-opacity"
                style={{ background: 'linear-gradient(135deg, #0EA5E9, #4F46E5)' }}>
                <Sparkles className="h-3.5 w-3.5" /> Complete Academic Profile
              </a>
            </div>
          </div>
        )}
        {tab === 'academics' && (
          <div className="grid gap-3 sm:grid-cols-2">
            {[
              { label: 'Highest Degree',       value: levelLabel },
              { label: 'Graduation Rate',       value: pct(s.graduation_rate) },
              { label: 'Retention Rate',        value: pct(s.retention_rate) },
              { label: 'Undergraduate Enrollment', value: s.size?.toLocaleString() || '-' },
            ].map(st => (
              <div key={st.label} className="rounded-xl border border-white/[0.08] bg-white/[0.04] p-4">
                <p className="text-[10.5px] text-slate-300 uppercase tracking-wide mb-1">{st.label}</p>
                <p className="text-xl font-bold text-white">{st.value}</p>
              </div>
            ))}
          </div>
        )}
        {tab === 'admissions' && (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { label: 'Acceptance Rate', value: pct(s.acceptance_rate), note: s.acceptance_rate < 25 ? 'Highly selective' : s.acceptance_rate < 50 ? 'Selective' : 'Accessible' },
              { label: 'SAT Average',     value: s.sat_average ? String(s.sat_average) : '-', note: sat ? `Your score: ${sat} (${satDelta >= 0 ? '+' : ''}${satDelta})` : 'Add your SAT score' },
              { label: 'Graduation Rate', value: pct(s.graduation_rate), note: null },
              { label: 'Retention Rate',  value: pct(s.retention_rate),  note: 'First-year students who return' },
              { label: 'Enrollment',      value: s.size?.toLocaleString() || '-', note: 'Undergraduate students' },
            ].map(st => (
              <div key={st.label} className="rounded-xl border border-white/[0.08] bg-white/[0.04] p-4">
                <p className="text-[10.5px] text-slate-300 uppercase tracking-wide mb-1">{st.label}</p>
                <p className="text-2xl font-bold text-white mb-1">{st.value}</p>
                {st.note && <p className="text-[11px] text-slate-300">{st.note}</p>}
              </div>
            ))}
          </div>
        )}
        {tab === 'cost_&_aid' && (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { label: 'In-State Tuition',  value: money(s.tuition_in_state),    note: 'Published tuition only' },
              { label: 'Out-of-State',       value: money(s.tuition_out_of_state), note: 'For non-residents' },
              { label: 'Avg Net Price',      value: money(s.avg_net_price),        note: 'After grants & scholarships' },
              { label: 'Median Debt',        value: money(s.median_debt),          note: 'Federal loans at graduation' },
              { label: 'Pell Grant Rate',    value: pct(s.pell_grant_rate),        note: 'Students receiving Pell grants' },
            ].map(st => (
              <div key={st.label} className="rounded-xl border border-white/[0.08] bg-white/[0.04] p-4">
                <p className="text-[10.5px] text-slate-300 uppercase tracking-wide mb-1">{st.label}</p>
                <p className="text-2xl font-bold text-white mb-1">{st.value || '-'}</p>
                <p className="text-[11px] text-slate-300">{st.note}</p>
              </div>
            ))}
          </div>
        )}
        {tab === 'career_outcomes' && (
          <div className="grid gap-3 sm:grid-cols-3">
            {[
              { label: 'Median Earnings 6yr',  value: money(s.earnings_6yr),  color: '#22d3ee', note: '6 years after enrollment' },
              { label: 'Median Earnings 10yr', value: money(s.earnings_10yr), color: '#34d399', note: '10 years after enrollment' },
              { label: 'Career ROI Score',     value: careerROI ? String(careerROI) : '-', color: '#34d399', note: 'Based on earnings vs $55K national median' },
            ].map(st => (
              <div key={st.label} className="rounded-xl border border-white/[0.08] bg-white/[0.04] p-5 text-center">
                <p className="text-[10.5px] text-slate-300 uppercase tracking-wide mb-2">{st.label}</p>
                <p className="text-3xl font-black mb-1" style={{ color: st.color }}>{st.value}</p>
                <p className="text-[11px] text-slate-300">{st.note}</p>
              </div>
            ))}
          </div>
        )}
        {tab === 'campus_life' && (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { label: 'Undergraduate Enrollment', value: s.size?.toLocaleString() || '-', note: `${campusSize} campus` },
              { label: 'Setting',        value: setting,                    note: `${s.city}, ${s.state}` },
              { label: 'Institution Type', value: `${typeLabel} · ${levelLabel}`, note: 'Control and degree level' },
              { label: 'Retention Rate', value: pct(s.retention_rate),      note: 'First-year students who return' },
              { label: 'Pell Grant Rate', value: pct(s.pell_grant_rate),    note: 'Share of students receiving Pell' },
            ].map(st => (
              <div key={st.label} className="rounded-xl border border-white/[0.08] bg-white/[0.04] p-4">
                <p className="text-[10.5px] text-slate-300 uppercase tracking-wide mb-1">{st.label}</p>
                <p className="text-xl font-bold text-white mb-1">{st.value || '-'}</p>
                <p className="text-[11px] text-slate-300">{st.note}</p>
              </div>
            ))}
          </div>
        )}
        {tab === 'athletics' && (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { label: 'NCAA Division', value: typeLabel === 'Public' ? 'Division I' : 'Division II / III', note: 'Typical for this institution type' },
              { label: 'Campus Size',   value: campusSize,                  note: 'Drives athletics program scale' },
              { label: 'Enrollment',    value: s.size?.toLocaleString() || '-', note: 'Undergraduate students' },
            ].map(st => (
              <div key={st.label} className="rounded-xl border border-white/[0.08] bg-white/[0.04] p-4">
                <p className="text-[10.5px] text-slate-300 uppercase tracking-wide mb-1">{st.label}</p>
                <p className="text-xl font-bold text-white mb-1">{st.value}</p>
                <p className="text-[11px] text-slate-300">{st.note}</p>
              </div>
            ))}
            <div className="sm:col-span-2 lg:col-span-3 rounded-xl border border-amber-400/25 bg-amber-400/[0.06] p-4">
              <p className="text-[12px] text-amber-200/90">
                Athletics detail is not published in the federal College Scorecard. FuturePath infers division from institution type and size, confirm with the school before making a decision.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* ── FuturePath Recommends ── */}
      <div className="glass-tint glass-violet rounded-2xl p-5 mb-4 flex items-start justify-between gap-5 flex-wrap"
        data-testid="futurepath-recommends">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.18em] text-violet-300 font-bold mb-2">FuturePath Recommends</p>
          <p className="text-[15px] font-bold text-white">Compare {shortName} with your other top matches</p>
          <p className="text-[12.5px] text-slate-300 mt-1 max-w-2xl leading-snug">
            {fpFit >= 75
              ? `${shortName} scores well against your goals. Put it side by side with your saved schools to see how cost and career outcomes differ.`
              : `${shortName} has trade-offs worth checking. Compare it against your saved schools before adding it to your plan.`}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={e => onCompare(s, e)} data-testid="recommends-compare-btn"
            className="flex items-center gap-2 rounded-lg px-4 py-2.5 text-[12.5px] font-semibold text-white hover:opacity-90 transition-opacity"
            style={{ background: 'linear-gradient(135deg, #7C3AED, #4F46E5)' }}>
            <BarChart3 className="h-4 w-4" /> Compare Colleges
          </button>
          <a href="/app/roadmap" className="text-[12.5px] font-medium text-violet-300 hover:text-violet-200 transition-colors px-2">
            View My Roadmap →
          </a>
        </div>
      </div>
    </div>
  );
}

// ── School Card (module-level, MUST be outside the main component for logos to work) ──
function SchoolCard({
  sc, onOpen, isSchoolSaved, onSave, inCompare, onCompare,
  prob, probLoad, onFetchProb, schoolRoi,
}) {
  // ROI-per-dollar: for every $1 spent (net-price × 4yrs), the graduate takes home
  // $X in lifetime earnings above what they would have without a degree. Comes
  // straight from the /roi endpoint; both values are federal data (Scorecard).
  const roiRatio = (() => {
    const inv = schoolRoi?.total_investment;
    const gain = schoolRoi?.net_gain;
    if (!inv || !gain || inv <= 0) return null;
    return gain / inv;
  })();

  return (
    <div className="glass-tint glass-cyan p-5 relative group" data-testid={`college-${sc.id || sc.name}`}>
      <div className="absolute top-4 right-4 flex items-center gap-1.5 z-10">
        <button onClick={(e) => onSave(sc, e)} data-testid={`save-${sc.id || sc.name}`}
          title={isSchoolSaved ? 'Remove from my list' : 'Save to my list'}
          className="p-1.5 rounded-lg hover:bg-white/10 transition-colors">
          <Star className={`w-4 h-4 ${isSchoolSaved ? 'text-amber-300 fill-amber-300' : 'text-slate-300'}`} />
        </button>
        <button onClick={(e) => onCompare(sc, e)} data-testid={`compare-${sc.id || sc.name}`}
          title="Add to compare"
          className="p-1.5 rounded-lg hover:bg-white/10 transition-colors">
          <Scale className={`w-4 h-4 ${inCompare ? 'text-violet-300' : 'text-slate-300'}`} />
        </button>
      </div>

      <button onClick={() => sc.id && onOpen(sc.id)} className="text-left w-full" data-testid={`open-${sc.id}`}>
        <div className="flex items-center gap-3 mb-3 pr-16">
          <SchoolLogo sc={sc} h={40} w={40} rounded="rounded-lg" />
          <div className="min-w-0">
            <p className="text-[14px] font-semibold text-white leading-snug group-hover:text-cyan-200 transition-colors truncate">
              {sc.name}
            </p>
            <p className="text-[11.5px] text-slate-300 mt-0.5">{sc.city}, {sc.state} · {sc.ownership}</p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {[
            { label: 'In-state',  value: money(sc.tuition_in_state) },
            { label: 'Accept',    value: pct(sc.acceptance_rate) },
            { label: 'Grad rate', value: pct(sc.graduation_rate) },
          ].map(m => (
            <div key={m.label} className="rounded-lg bg-white/[0.06] p-2.5 text-center">
              <p className="text-slate-300 text-[10px] uppercase tracking-wide mb-0.5">{m.label}</p>
              <p className="text-white text-[13px] font-semibold">{m.value}</p>
            </div>
          ))}
        </div>

        {/* Auto-surfaced signal chips, return per dollar + odds */}
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          {roiRatio != null && (
            <span
              className="inline-flex items-center gap-1 rounded-full border border-cyan-300/40 bg-cyan-400/[0.08] px-2 py-0.5 text-[11px] font-semibold text-cyan-100"
              title={`Estimated $${roiRatio.toFixed(2)} in lifetime earnings above no-degree baseline for every $1 spent (cost × 4 yrs vs. lifetime earnings, U.S. Dept. of Education College Scorecard).`}
              data-testid={`roi-per-dollar-${sc.id}`}
              style={{ boxShadow: '0 0 22px -10px rgba(34,211,238,0.7)' }}
            >
              <DollarSign className="w-3 h-3" />
              ${roiRatio.toFixed(1)} back / $1
            </span>
          )}
          {prob ? (
            <span
              className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${PROB_COLORS[prob.band] || PROB_COLORS.unknown}`}
              title={`${prob.label} · matched to your GPA / test scores against the acceptance rate on file`}
              data-testid={`odds-${sc.id}`}
            >
              <Zap className="w-3 h-3" />
              Odds · {prob.label}
            </span>
          ) : probLoad ? (
            <span className="inline-flex items-center gap-1 rounded-full border border-slate-600/50 bg-slate-800/50 px-2 py-0.5 text-[11px] text-slate-300">
              <Zap className="w-3 h-3 animate-pulse" /> Odds…
            </span>
          ) : null}
          <span
            className="ml-auto text-[10px] text-slate-300 hover:text-cyan-300 cursor-help"
            title="ROI ratio = expected lifetime earnings (Dept. of Education post-college earnings data) ÷ 4-year net cost after aid. Odds are your GPA & test scores against this school's published acceptance rate."
          >
            What's this?
          </span>
        </div>
      </button>
    </div>
  );
}


const SOURCE = {
  headline: 'U.S. Department of Education, College Scorecard',
  lines: [
    ['Where the numbers come from', 'Every figure on this page is read live from the federal College Scorecard API (collegescorecard.ed.gov). FuturePath does not estimate, adjust or model any of it.'],
    ['Cost, admissions, graduation, retention', 'Reported by each institution to the Department through IPEDS, the survey colleges are legally required to complete. Net price is the average families actually paid after grants and scholarships.'],
    ['Earnings after college', 'Not a survey and not self-reported: the Department matches student records to federal tax data and publishes medians. Institution-level figures are median earnings 6 and 10 years after entry.'],
    ['Earnings by major', 'From the Scorecard Field of Study file, median earnings of graduates of that specific program at that specific school, from tax records.'],
    ['Median debt', 'Federal student loan balances at completion, taken from the Department\'s own loan records.'],
  ],
  caveats: [
    'Earnings cover students who received federal financial aid, the group the Department can follow in tax records.',
    'Programs with too few graduates are suppressed by the Department for privacy.',
    'Tuition is the published price; net price is what the average aided family paid.',
    'The Department refreshes the Scorecard annually, so data typically lags the current year by one to two years.',
  ],
};


export default function CollegeIntelPage() {
  const { getAuthHeader, user } = useAuth();
  const [q, setQ]               = useState('');
  const [schools, setSchools]   = useState([]);
  const [detail, setDetail]     = useState(null);
  const [impact, setImpact]     = useState(null);
  const [semesters, setSemesters] = useState(2);
  const [residency, setResidency] = useState('in_state');
  const [loading, setLoading]   = useState(false);
  const [collection, setCollection] = useState('');
  const [collections, setCollections] = useState([]);
  const [meta, setMeta]         = useState(null);
  const [listFilter, setListFilter] = useState('');
  const [shown, setShown]       = useState(24);
  const [tab, setTab]           = useState('explore');
  const [saved, setSaved]       = useState([]);
  const [compare, setCompare]   = useState([]);
  const [roi, setRoi]           = useState({});
  const [showSource, setShowSource] = useState(false);
  const [matching, setMatching] = useState(false);
  const [probabilities, setProbabilities] = useState({});
  const [probLoading, setProbLoading]     = useState({});
  const [costSummary, setCostSummary]     = useState(null);
  const [showFilters, setShowFilters]     = useState(true);
  const [advFilters, setAdvFilters]       = useState({
    state: '', ownership: [], degree_type: '', major: '',
    max_acceptance_rate: null, sat_min: null, sat_max: null,
    tuition_max: null, size: [], setting: [],
    hbcu_only: false, hsi_only: false,
    sort: 'size_desc',
  });
  const advFilterActive = advFilters.state || advFilters.ownership.length || advFilters.degree_type ||
    advFilters.major || advFilters.max_acceptance_rate || advFilters.sat_min || advFilters.sat_max ||
    advFilters.tuition_max || advFilters.size.length || advFilters.setting.length ||
    advFilters.hbcu_only || advFilters.hsi_only;
  const reqId = useRef(0);

  // Fetch cost model summary for profile-based fit scores
  useEffect(() => {
    axios.get(`${API}/cost-model/summary`, getAuthHeader())
      .then(r => setCostSummary(r.data))
      .catch(() => {});
  }, [getAuthHeader]);

  const loadSaved = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/colleges/favorites`, getAuthHeader());
      setSaved(r.data.favorites || []);
    } catch { /* ignored */ }
  }, [getAuthHeader]);

  useEffect(() => { loadSaved(); }, [loadSaved]);

  const priced    = [...saved, ...compare].filter((s) => s?.id);
  const pricedKey = priced.map((s) => s.id).sort().join(',');
  useEffect(() => {
    if (!pricedKey) return;
    const unique = Object.values(Object.fromEntries(priced.map((s) => [s.id, s])));
    axios.post(`${API}/education-roi/schools`, { schools: unique.slice(0, 6) }, getAuthHeader())
      .then((r) => setRoi(Object.fromEntries((r.data.schools || []).map((m) => [m.name, m]))))
      .catch(() => {});
  }, [pricedKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const isSaved      = (sc) => saved.some((f) => f.name === sc.name);

  const toggleSave = async (sc, e) => {
    e?.stopPropagation();
    try {
      if (isSaved(sc)) {
        await axios.delete(`${API}/colleges/favorites/${encodeURIComponent(sc.name)}`, getAuthHeader());
        setSaved((f) => f.filter((x) => x.name !== sc.name));
        toast.success(`Removed ${sc.name}`);
      } else {
        await axios.post(`${API}/colleges/favorites`, { college: sc }, getAuthHeader());
        setSaved((f) => [...f, sc]);
        if (saved.length === 0) celebrate(`${sc.name} is the first on your list.`);
        else toast.success(`Saved ${sc.name} to your list`);
      }
    } catch { toast.error('Could not update your saved list'); }
  };

  const toggleCompare = (sc, e) => {
    e?.stopPropagation();
    setCompare((c) => {
      if (c.some((x) => x.name === sc.name)) return c.filter((x) => x.name !== sc.name);
      if (c.length >= 4) { toast.error('Four at a time is the most that reads clearly'); return c; }
      return [...c, sc];
    });
  };

  const visible = listFilter.trim()
    ? schools.filter((sc) => `${sc.name} ${sc.state} ${sc.city}`.toLowerCase().includes(listFilter.trim().toLowerCase()))
    : schools;

  const loadCollections = useCallback(() => {
    axios.get(`${API}/college-intel/collections`, getAuthHeader())
      .then(r => setCollections(r.data.collections)).catch(() => {});
  }, [getAuthHeader]);

  const loadCollection = useCallback(async (key) => {
    const mine = ++reqId.current;
    setLoading(true); setDetail(null); setImpact(null);
    setCollection(key); setSchools([]); setListFilter(''); setShown(24);
    try {
      const r = await axios.get(`${API}/college-intel/collections/${key}`, getAuthHeader());
      if (mine !== reqId.current) return;
      setSchools(r.data.schools || []);
      setMeta(r.data);
    } catch { if (mine === reqId.current) toast.error('Federal data is unavailable right now'); }
    finally { if (mine === reqId.current) setLoading(false); }
  }, [getAuthHeader]);

  const loadByNames = useCallback(async (key, names) => {
    const mine = ++reqId.current;
    setLoading(true); setDetail(null); setImpact(null);
    setCollection(key); setSchools([]); setMeta(null); setListFilter(''); setShown(24);
    try {
      const r = await axios.get(`${API}/college-intel/batch`, { ...getAuthHeader(), params: { names: names.join('|') } });
      if (mine !== reqId.current) return;
      setSchools(r.data.schools || []);
    } catch { if (mine === reqId.current) toast.error('Federal data is unavailable right now'); }
    finally { if (mine === reqId.current) setLoading(false); }
  }, [getAuthHeader]);

  const loadState = useCallback(async (st) => {
    if (!st) return;
    const mine = ++reqId.current;
    setLoading(true); setDetail(null); setImpact(null);
    setCollection(`state-${st}`); setSchools([]);
    try {
      const r = await axios.get(`${API}/college-intel/search`, { ...getAuthHeader(), params: { q: '', state: st, limit: 24 } });
      if (mine !== reqId.current) return;
      setSchools(r.data.schools || []);
    } catch { if (mine === reqId.current) toast.error('Federal data is unavailable right now'); }
    finally { if (mine === reqId.current) setLoading(false); }
  }, [getAuthHeader]);

  const firstLoad = useRef(false);
  useEffect(() => {
    if (firstLoad.current) return;
    firstLoad.current = true;
    loadCollections();
    // Default to NC, student is a Wake County student. If they're not, we still
    // start with a coherent alphabetised list rather than "Most searched".
    loadCollection('nc');
  }, [loadCollection]); // eslint-disable-line react-hooks/exhaustive-deps

  const search = useCallback(async () => {
    if (!q.trim() && !advFilterActive) return toast.error('Enter a college name or use filters');
    const mine = ++reqId.current;
    setLoading(true); setDetail(null); setImpact(null); setCollection('');
    try {
      if (advFilterActive) {
        const payload = { ...advFilters, q: q.trim(), page: 0, per_page: 40 };
        const r = await axios.post(`${API}/college-intel/advanced-search`, payload, getAuthHeader());
        if (mine !== reqId.current) return;
        setSchools(r.data.schools || []);
        if (!r.data.schools?.length) toast.error('No colleges match those filters');
      } else {
        const r = await axios.get(`${API}/college-intel/search`, { ...getAuthHeader(), params: { q } });
        if (mine !== reqId.current) return;
        setSchools(r.data.schools || []);
        if (!r.data.schools?.length) toast.error('No institution matched that name');
      }
    } catch { if (mine === reqId.current) toast.error('The federal College Scorecard service did not respond'); }
    finally { if (mine === reqId.current) setLoading(false); }
  }, [q, getAuthHeader, advFilterActive, advFilters]);

  const clearAdvFilters = () => setAdvFilters({
    state: '', ownership: [], degree_type: '', major: '',
    max_acceptance_rate: null, sat_min: null, sat_max: null,
    tuition_max: null, size: [], setting: [],
    hbcu_only: false, hsi_only: false, sort: 'size_desc',
  });

  const toggleFilterArray = (key, val) => setAdvFilters(f => ({
    ...f, [key]: (f[key] || []).includes(val) ? f[key].filter(x => x !== val) : [...(f[key] || []), val],
  }));
  const setFilterField = (k, v) => setAdvFilters(f => ({ ...f, [k]: v }));

  const open = async (id) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/college-intel/school/${id}`, getAuthHeader());
      setDetail(r.data); setImpact(null);
    } catch { toast.error('Could not load that institution'); }
    finally { setLoading(false); }
  };

  const runImpact = async () => {
    try {
      const r = await axios.post(`${API}/college-intel/major-change-impact`,
        { school_id: detail.school.id, extra_semesters: Number(semesters), residency }, getAuthHeader());
      setImpact(r.data);
    } catch { toast.error('Could not calculate'); }
  };

  const recommend = async () => {
    setMatching(true);
    try {
      const r = await axios.post(`${API}/ai/college-match`, {}, getAuthHeader());
      const names = (r.data?.result?.colleges || []).map((c) => c.name).filter(Boolean);
      if (!names.length) { toast.error('No recommendations came back, try again shortly'); return; }
      setTab('explore');
      await loadByNames('recommended', names);
      toast.success('Matched to your profile.');
    } catch (e) {
      if (e?.response?.status === 402 || e?.response?.status === 429) return;
      toast.error('Could not build recommendations just now');
    } finally { setMatching(false); }
  };

  const fetchProbability = useCallback(async (sc, e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (!sc?.id) return;
    if (probabilities[sc.id] || probLoading[sc.id]) return;
    setProbLoading(p => ({ ...p, [sc.id]: true }));
    try {
      const r = await axios.get(`${API}/nc/admissions-probability/${sc.id}`, getAuthHeader());
      setProbabilities(p => ({ ...p, [sc.id]: r.data.probability }));
    } catch { /* silent, some accounts don't have profile data yet */ }
    finally { setProbLoading(p => ({ ...p, [sc.id]: false })); }
  }, [probabilities, probLoading, getAuthHeader]);

  // As soon as the visible list changes, kick off admissions-probability calls
  // for every card that doesn't have one yet. Batched (max 6 in flight) to keep
  // the API polite. Silent-fails so a probability endpoint hiccup won't blank
  // the whole page.
  useEffect(() => {
    const toFetch = (schools || []).filter(
      (sc) => sc?.id && !probabilities[sc.id] && !probLoading[sc.id]
    ).slice(0, 24);
    if (!toFetch.length) return;
    let cancelled = false;
    (async () => {
      for (let i = 0; i < toFetch.length; i += 6) {
        if (cancelled) return;
        await Promise.all(toFetch.slice(i, i + 6).map((sc) => fetchProbability(sc)));
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schools]);

  const s = detail?.school;

  return (
    <AppLayout>
      {/* ── Hero, compact, brand-consistent ── */}
      <div className="relative overflow-hidden rounded-2xl border border-cyan-500/25 bg-[#0a1729]/70 p-6 md:p-7"
        data-testid="college-hero" style={{ boxShadow: '0 40px 100px -60px rgba(34,211,238,0.6)' }}>
        <div aria-hidden className="pointer-events-none absolute -right-32 -top-24 h-64 w-64 rounded-full blur-3xl opacity-30"
          style={{ background: 'radial-gradient(circle, rgba(34,211,238,0.4), transparent 65%)' }} />
        <div aria-hidden className="pointer-events-none absolute -left-24 -bottom-24 h-64 w-64 rounded-full blur-3xl opacity-20"
          style={{ background: 'radial-gradient(circle, rgba(167,139,250,0.4), transparent 65%)' }} />

        <div className="relative flex flex-wrap items-center justify-between gap-4">
          <div className="min-w-0 flex-1">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-cyan-300/40 bg-cyan-400/10 px-2.5 py-0.5
              text-[10px] font-semibold uppercase tracking-[0.2em] text-cyan-200">
              <GraduationCap className="h-3 w-3" /> College Intelligence
            </span>
            <h1 className="font-outfit mt-2 text-2xl sm:text-3xl font-semibold text-white tracking-tight">
              Every U.S. college,{' '}
              <span className="bg-gradient-to-r from-cyan-300 via-sky-300 to-violet-400 bg-clip-text text-transparent">
                on the same numbers
              </span>
            </h1>
            <p className="mt-1.5 max-w-2xl text-sm text-slate-300/90 leading-relaxed">
              Real cost after aid, real graduation rates, and what graduates in each specific major actually earn, all from the federal record so you can compare apples to apples.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <button onClick={() => setShowFilters(v => !v)} data-testid="toggle-adv-filters"
                  className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold ring-1 transition-all duration-200 hover:-translate-y-0.5 ${
                    showFilters
                      ? 'text-white bg-cyan-500/20 ring-cyan-400/60 shadow-[0_0_20px_-6px_rgba(34,211,238,0.5)]'
                      : 'text-cyan-200 bg-cyan-500/10 ring-cyan-400/40 hover:bg-cyan-500/15'}`}>
              <Filter className="w-4 h-4" />
              {showFilters ? 'Hide filters' : 'Show filters'}
              {advFilterActive && <span className="ml-1 w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />}
            </button>
            <Button onClick={recommend} disabled={matching} data-testid="recommend-btn"
              className="rounded-full px-5 py-2 text-sm text-white bg-gradient-to-r from-cyan-500 to-violet-500"
              style={{ boxShadow: '0 0 26px -8px rgba(34,211,238,0.55)' }}>
              {matching ? <LoadingSpinner size="sm" /> : <><Sparkles className="w-4 h-4 mr-1.5" />Match to me</>}
            </Button>
            <button onClick={() => setShowSource((v) => !v)} data-testid="source-toggle"
              className="inline-flex items-center gap-1.5 text-xs text-slate-300 hover:text-cyan-200 transition-colors">
              <ShieldCheck className="w-3.5 h-3.5" />About the data
            </button>
          </div>
        </div>
      </div>

      {showSource && (
        <GlassCard className="mt-4" data-testid="source-panel">
          <p className="text-xs uppercase tracking-wider text-cyan-300 font-semibold">{SOURCE.headline}</p>
          <div className="grid md:grid-cols-2 gap-4 mt-4">
            {SOURCE.lines.map(([h, b]) => (
              <div key={h} className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                <p className="text-sm font-semibold text-white">{h}</p>
                <p className="text-xs text-slate-300/85 mt-1.5 leading-relaxed">{b}</p>
              </div>
            ))}
          </div>
          <p className="text-sm font-semibold text-white mt-5">What these numbers can't tell you</p>
          <ul className="mt-2 space-y-1.5">
            {SOURCE.caveats.map((c) => (
              <li key={c} className="text-xs text-slate-300/85 flex gap-2"><span className="text-amber-300">-</span>{c}</li>
            ))}
          </ul>
        </GlassCard>
      )}

      {/* ── Tabs ── */}
      <div className="flex flex-wrap gap-2 mt-6" data-testid="college-tabs">
        {[['explore', 'Explore'], ['saved', `My list${saved.length ? ` · ${saved.length}` : ''}`],
          ['compare', `Compare${compare.length ? ` · ${compare.length}` : ''}`],
          ['roi', 'Education investment']].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`px-4 py-2 rounded-full text-sm font-medium border transition-all duration-200 ${
              tab === k ? 'border-cyan-300/60 bg-cyan-300/15 text-white' : 'border-white/12 text-slate-300 hover:text-white'}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'explore' && (
        <GlassCard className="mt-4">
          {/* Search sits on the left, filters (state + collections) live on the right, user's request */}
          <div className="flex flex-col lg:flex-row gap-4 items-stretch">
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-wider text-slate-300 font-semibold mb-2">Search any U.S. college</p>
              <div className="flex gap-2">
                <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && search()}
                  placeholder="Type a college name…" className="input-dark flex-1" data-testid="college-intel-search" />
                <Button onClick={search} disabled={loading} className="btn-primary" data-testid="college-intel-search-btn">
                  {loading ? <LoadingSpinner size="sm" /> : 'Search'}
                </Button>
              </div>
            </div>
            <div className="lg:w-72 shrink-0">
              <p className="text-xs uppercase tracking-wider text-slate-300 font-semibold mb-2">Browse by state</p>
              <select onChange={(e) => loadState(e.target.value)} defaultValue="" className="input-dark text-sm py-2 w-full">
                <option value="">Any state…</option>
                {STATES.map((st) => <option key={st} value={st}>{st}</option>)}
              </select>
            </div>
          </div>

          {/* Inline Advanced Filters, always visible by default, glowing buttons */}
          {showFilters && (
            <div className="mt-4 p-5 rounded-xl border border-cyan-500/20 bg-cyan-500/[0.03] space-y-4" data-testid="inline-filters"
              style={{ boxShadow: '0 0 30px -10px rgba(34,211,238,0.15)' }}>
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-wider text-cyan-300 font-semibold">Filter colleges</span>
                <div className="flex items-center gap-2">
                  {advFilterActive && (
                    <button onClick={clearAdvFilters} className="text-xs text-slate-300 hover:text-white flex items-center gap-1" data-testid="clear-adv-filters">
                      <X className="w-3 h-3" /> Clear all
                    </button>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                  <label className="text-[10px] uppercase text-slate-300 font-semibold mb-1 block">State</label>
                  <select value={advFilters.state} onChange={(e) => setFilterField('state', e.target.value)}
                    data-testid="adv-filter-state"
                    className="w-full px-2.5 py-2 rounded-lg bg-slate-800 text-white text-sm ring-1 ring-cyan-400/20 focus:ring-cyan-400 focus:outline-none appearance-none cursor-pointer">
                    <option value="">Any state</option>
                    {['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC','PR'].map(st => (
                      <option key={st} value={st}>{st}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase text-slate-300 font-semibold mb-1 block">Major / Program</label>
                  <select value={advFilters.major || ''} onChange={(e) => setFilterField('major', e.target.value)}
                    data-testid="adv-filter-major"
                    className="w-full px-2.5 py-2 rounded-lg bg-slate-800 text-white text-sm ring-1 ring-cyan-400/20 focus:ring-cyan-400 focus:outline-none appearance-none cursor-pointer">
                    <option value="">Any major</option>
                    {['Accounting','Agriculture','Architecture','Biology','Business Administration','Chemistry','Communications','Computer Science','Criminal Justice','Economics','Education','Engineering','English','Environmental Science','Finance','Fine Arts','Health Sciences','History','Information Technology','International Relations','Journalism','Kinesiology','Law','Liberal Arts','Marketing','Mathematics','Mechanical Engineering','Music','Nursing','Philosophy','Physics','Political Science','Psychology','Public Health','Social Work','Sociology','Theater','Undecided'].map(m => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase text-slate-300 font-semibold mb-1 block">Max acceptance rate</label>
                  <select value={advFilters.max_acceptance_rate ? Math.round(advFilters.max_acceptance_rate * 100) : ''}
                    onChange={(e) => setFilterField('max_acceptance_rate', e.target.value ? Number(e.target.value) / 100 : null)}
                    data-testid="adv-filter-accept"
                    className="w-full px-2.5 py-2 rounded-lg bg-slate-800 text-white text-sm ring-1 ring-cyan-400/20 focus:ring-cyan-400 focus:outline-none appearance-none cursor-pointer">
                    <option value="">Any</option>
                    <option value="10">Under 10%</option>
                    <option value="20">Under 20%</option>
                    <option value="30">Under 30%</option>
                    <option value="50">Under 50%</option>
                    <option value="70">Under 70%</option>
                    <option value="90">Under 90%</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase text-slate-300 font-semibold mb-1 block">Max tuition (per year)</label>
                  <select value={advFilters.tuition_max || ''}
                    onChange={(e) => setFilterField('tuition_max', e.target.value ? Number(e.target.value) : null)}
                    data-testid="adv-filter-tuition"
                    className="w-full px-2.5 py-2 rounded-lg bg-slate-800 text-white text-sm ring-1 ring-cyan-400/20 focus:ring-cyan-400 focus:outline-none appearance-none cursor-pointer">
                    <option value="">Any</option>
                    <option value="5000">Under $5,000</option>
                    <option value="10000">Under $10,000</option>
                    <option value="15000">Under $15,000</option>
                    <option value="20000">Under $20,000</option>
                    <option value="30000">Under $30,000</option>
                    <option value="40000">Under $40,000</option>
                    <option value="50000">Under $50,000</option>
                    <option value="60000">Under $60,000</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                  <label className="text-[10px] uppercase text-slate-300 font-semibold mb-1 block">SAT range</label>
                  <div className="flex gap-2">
                    <select value={advFilters.sat_min || ''}
                      onChange={(e) => setFilterField('sat_min', e.target.value ? Number(e.target.value) : null)}
                      data-testid="adv-filter-sat-min"
                      className="flex-1 px-2.5 py-2 rounded-lg bg-slate-800 text-white text-sm ring-1 ring-cyan-400/20 focus:ring-cyan-400 focus:outline-none appearance-none cursor-pointer">
                      <option value="">Min</option>
                      {[800,900,1000,1100,1200,1300,1400,1500].map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                    <select value={advFilters.sat_max || ''}
                      onChange={(e) => setFilterField('sat_max', e.target.value ? Number(e.target.value) : null)}
                      data-testid="adv-filter-sat-max"
                      className="flex-1 px-2.5 py-2 rounded-lg bg-slate-800 text-white text-sm ring-1 ring-cyan-400/20 focus:ring-cyan-400 focus:outline-none appearance-none cursor-pointer">
                      <option value="">Max</option>
                      {[1000,1100,1200,1300,1400,1500,1600].map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-[10px] uppercase text-slate-300 font-semibold mb-1 block">Degree type</label>
                  <select value={advFilters.degree_type || ''}
                    onChange={(e) => setFilterField('degree_type', e.target.value)}
                    data-testid="adv-filter-degree"
                    className="w-full px-2.5 py-2 rounded-lg bg-slate-800 text-white text-sm ring-1 ring-cyan-400/20 focus:ring-cyan-400 focus:outline-none appearance-none cursor-pointer">
                    <option value="">Any</option>
                    <option value="certificate">Certificate</option>
                    <option value="associate">Associate</option>
                    <option value="bachelors">Bachelor's</option>
                    <option value="graduate">Graduate</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase text-slate-300 font-semibold mb-1 block">Setting</label>
                  <div className="flex flex-wrap gap-1.5">
                    {[['city','City'], ['suburban','Suburban'], ['town','Town'], ['rural','Rural']].map(([v, l]) => (
                      <button key={v} onClick={() => toggleFilterArray('setting', v)}
                        data-testid={`adv-setting-${v}`}
                        className={`px-2.5 py-1 rounded-full text-xs font-semibold ring-1 transition-all duration-200 hover:-translate-y-0.5 ${
                          (advFilters.setting || []).includes(v)
                            ? 'bg-cyan-500/25 text-cyan-100 ring-cyan-400/60 shadow-[0_0_12px_-4px_rgba(34,211,238,0.5)]'
                            : 'bg-slate-800 text-slate-300 ring-slate-700 hover:ring-cyan-400/40'}`}>
                        {l}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-[10px] uppercase text-slate-300 font-semibold mb-1 block">Sort results by</label>
                  <select value={advFilters.sort || 'size_desc'}
                    onChange={(e) => setFilterField('sort', e.target.value)}
                    data-testid="adv-filter-sort"
                    className="w-full px-2.5 py-2 rounded-lg bg-slate-800 text-white text-sm ring-1 ring-cyan-400/20 focus:ring-cyan-400 focus:outline-none appearance-none cursor-pointer">
                    <option value="size_desc">Largest first</option>
                    <option value="size_asc">Smallest first</option>
                    <option value="acceptance_asc">Most selective</option>
                    <option value="acceptance_desc">Easiest to get in</option>
                    <option value="tuition_asc">Lowest tuition</option>
                    <option value="tuition_desc">Highest tuition</option>
                  </select>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <label className="text-[10px] uppercase text-slate-300 font-semibold">Institution type</label>
                {[[1,'Public'], [2,'Private nonprofit'], [3,'For-profit']].map(([v, l]) => (
                  <button key={v} onClick={() => toggleFilterArray('ownership', v)}
                    data-testid={`adv-ownership-${v}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold ring-1 transition-all duration-200 hover:-translate-y-0.5 ${
                      advFilters.ownership.includes(v)
                        ? 'bg-cyan-500/25 text-cyan-100 ring-cyan-400/60 shadow-[0_0_12px_-4px_rgba(34,211,238,0.5)]'
                        : 'bg-slate-800 text-slate-300 ring-slate-700 hover:ring-cyan-400/40'}`}>
                    {l}
                  </button>
                ))}
                <span className="mx-2 text-slate-300">|</span>
                <label className="text-[10px] uppercase text-slate-300 font-semibold">Campus size</label>
                {[['small','Small (< 3K)'], ['medium','Medium (3-10K)'], ['large','Large (10K+)']].map(([v, l]) => (
                  <button key={v} onClick={() => toggleFilterArray('size', v)}
                    data-testid={`adv-size-${v}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold ring-1 transition-all duration-200 hover:-translate-y-0.5 ${
                      advFilters.size.includes(v)
                        ? 'bg-cyan-500/25 text-cyan-100 ring-cyan-400/60 shadow-[0_0_12px_-4px_rgba(34,211,238,0.5)]'
                        : 'bg-slate-800 text-slate-300 ring-slate-700 hover:ring-cyan-400/40'}`}>
                    {l}
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <label className="text-[10px] uppercase text-slate-300 font-semibold">Identity</label>
                <button onClick={() => setFilterField('hbcu_only', !advFilters.hbcu_only)}
                  data-testid="adv-hbcu"
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold ring-1 transition-all duration-200 hover:-translate-y-0.5 ${
                    advFilters.hbcu_only
                      ? 'bg-cyan-500/25 text-cyan-100 ring-cyan-400/60 shadow-[0_0_12px_-4px_rgba(34,211,238,0.5)]'
                      : 'bg-slate-800 text-slate-300 ring-slate-700 hover:ring-cyan-400/40'}`}>
                  HBCU
                </button>
                <button onClick={() => setFilterField('hsi_only', !advFilters.hsi_only)}
                  data-testid="adv-hsi"
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold ring-1 transition-all duration-200 hover:-translate-y-0.5 ${
                    advFilters.hsi_only
                      ? 'bg-cyan-500/25 text-cyan-100 ring-cyan-400/60 shadow-[0_0_12px_-4px_rgba(34,211,238,0.5)]'
                      : 'bg-slate-800 text-slate-300 ring-slate-700 hover:ring-cyan-400/40'}`}>
                  HSI
                </button>
                <Button onClick={search} disabled={loading} size="sm" className="ml-auto btn-primary text-xs px-5" data-testid="apply-filters-btn">
                  {loading ? <LoadingSpinner size="sm" /> : 'Search with filters'}
                </Button>
              </div>
            </div>
          )}

          <p className="text-xs uppercase tracking-wider text-slate-300 font-semibold mt-5 mb-2">Quick filters</p>
          <div className="flex flex-wrap gap-2" data-testid="college-collections">
            {collections.map((c) => {
              const key = c.key;
              const hue = COLLECTION_HUE[key] || '#22d3ee';
              const on  = collection === key;
              return (
                <button key={key} onClick={() => loadCollection(key)} data-testid={`collection-${key}`}
                  className="px-4 py-2 rounded-full text-sm font-semibold border transition-all duration-200 hover:-translate-y-0.5"
                  style={on
                    ? { borderColor: hue, background: `${hue}26`, color: '#fff', boxShadow: `0 0 28px -8px ${hue}` }
                    : { borderColor: `${hue}44`, color: hue }}>
                  {c.label}
                  {c.count ? <span className="ml-1.5 opacity-70 fp-num">{c.count}</span> : null}
                </button>
              );
            })}
          </div>
        </GlassCard>
      )}

      {tab === 'explore' && loading && !detail && (
        <div className="py-16 flex justify-center"><LoadingSpinner /></div>
      )}

      {tab === 'explore' && schools.length > 0 && !detail && (
        <>
          {schools.length > 12 && (
            <div className="mt-4">
              <Input value={listFilter} onChange={(e) => setListFilter(e.target.value)}
                placeholder={`Filter these ${schools.length} colleges, name or state`}
                className="input-dark" />
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-4 mt-4" data-testid="college-intel-results">
            {visible.slice(0, shown).map((sc) => (
              <SchoolCard
                key={sc.id || sc.name}
                sc={sc}
                onOpen={open}
                isSchoolSaved={isSaved(sc)}
                onSave={toggleSave}
                inCompare={compare.some(x => x.name === sc.name)}
                onCompare={toggleCompare}
                prob={probabilities[sc.id]}
                probLoad={probLoading[sc.id]}
                onFetchProb={fetchProbability}
                schoolRoi={roi[sc?.name]}
              />
            ))}
          </div>
          {visible.length > shown && (
            <div className="flex justify-center mt-5">
              <Button onClick={() => setShown((n) => n + 24)} className="rounded-full bg-slate-800 hover:bg-slate-700 text-white">
                Show {Math.min(24, visible.length - shown)} more of {visible.length}
              </Button>
            </div>
          )}
        </>
      )}

      {tab === 'saved' && !detail && (
        saved.length > 0 ? (
          <>
            <div className="flex flex-wrap items-center gap-3 mt-4">
              <p className="text-sm text-slate-300">{saved.length} on your list.</p>
              <button onClick={() => { setCompare(saved.slice(0, 4)); setTab('compare'); }}
                className="px-4 py-1.5 rounded-full text-xs font-medium bg-cyan-400/15 border border-cyan-300/40 text-cyan-100 hover:bg-cyan-400/25 transition-colors">
                Compare these side by side
              </button>
            </div>
            <div className="grid md:grid-cols-2 gap-4 mt-3" data-testid="saved-results">
              {saved.map((sc) => (
                <SchoolCard
                  key={sc.name}
                  sc={sc}
                  onOpen={open}
                  isSchoolSaved={true}
                  onSave={toggleSave}
                  inCompare={compare.some(x => x.name === sc.name)}
                  onCompare={toggleCompare}
                  prob={probabilities[sc.id]}
                  probLoad={probLoading[sc.id]}
                  onFetchProb={fetchProbability}
                  schoolRoi={roi[sc?.name]}
                />
              ))}
            </div>
          </>
        ) : (
          <GlassCard className="mt-4">
            <p className="text-sm text-slate-300">Nothing saved yet. Tap the star on any college card.</p>
          </GlassCard>
        )
      )}

      {tab === 'roi' && !detail && <EducationInvestment />}

      {tab === 'compare' && (
        compare.length > 0 ? (
          <GlassCard className="mt-4 overflow-x-auto" data-testid="compare-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-300 border-b border-white/10">
                  <th className="py-2.5 pr-4">Measure</th>
                  {compare.map((c) => <th key={c.name} className="py-2.5 pr-4 text-white normal-case text-sm">{c.name}</th>)}
                </tr>
              </thead>
              <tbody>
                {[
                  ['In-state tuition',   (c) => money(c.tuition_in_state)],
                  ['Out-of-state',        (c) => money(c.tuition_out_of_state)],
                  ['Average net price',  (c) => money(c.avg_net_price)],
                  ['Acceptance rate',    (c) => pct(c.acceptance_rate)],
                  ['Graduation rate',    (c) => pct(c.graduation_rate)],
                  ['Earnings 10yr',      (c) => money(c.earnings_10yr)],
                  ['Median debt',        (c) => money(c.median_debt)],
                  ['Total investment',   (c) => money(roi[c?.name]?.total_investment)],
                  ['Net lifetime gain',  (c) => money(roi[c?.name]?.net_gain)],
                  ['Verdict',            (c) => roi[c?.name]?.verdict || '-'],
                ].map(([label, get]) => (
                  <tr key={label} className="border-b border-white/5">
                    <td className="py-2.5 pr-4 text-slate-300">{label}</td>
                    {compare.map((c) => <td key={c.name} className="py-2.5 pr-4 text-white">{get(c)}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex flex-wrap gap-2 mt-4">
              {compare.map((c) => (
                <button key={c.name} onClick={(e) => toggleCompare(c, e)}
                  className="px-3 py-1.5 rounded-full text-xs border border-white/15 text-slate-300 hover:text-white">
                  Remove {c.name}
                </button>
              ))}
            </div>
          </GlassCard>
        ) : (
          <GlassCard className="mt-4">
            <p className="text-sm text-slate-300">Add up to four colleges with the scales icon.</p>
            {saved.length > 0 && (
              <button onClick={() => setCompare(saved.slice(0, 4))}
                className="mt-3 px-4 py-2 rounded-full text-sm font-medium bg-cyan-400/15 border border-cyan-300/40 text-cyan-100">
                Compare the {Math.min(saved.length, 4)} on my list
              </button>
            )}
          </GlassCard>
        )
      )}

      {s && (
        <>
          <CollegeDetailView
            college={s}
            user={user}
            costSummary={costSummary}
            isSaved={isSaved}
            onSave={toggleSave}
            onBack={() => setDetail(null)}
            onCompare={toggleCompare}
            inCompare={compare.some(x => x.name === s.name)}
          />

          {detail.programs?.length > 0 && (
            <GlassCard className="mt-4">
              <h3 className="font-outfit text-xl font-semibold text-white flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-emerald-300" />What each major actually earns
              </h3>
              <p className="text-sm text-slate-300/80 mt-1">
                {detail.program_count} programs reported at {s.name}, ranked by median earnings.
              </p>
              <div className="mt-5 overflow-x-auto">
                <table className="w-full text-sm" data-testid="program-earnings-table">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wider text-slate-300 border-b border-white/10">
                      <th className="py-2.5 pr-4">Program</th>
                      <th className="py-2.5 pr-4">Credential</th>
                      <th className="py-2.5 pr-4">Median earnings</th>
                      {detail.programs.some((p) => p.median_debt) && <th className="py-2.5 pr-4">Median debt</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {detail.programs.slice(0, 40).map((p, i) => (
                      <tr key={`${p.title}-${i}`} className="border-b border-white/5 hover:bg-white/[0.03] transition-colors">
                        <td className="py-2.5 pr-4 text-slate-100">{p.title}</td>
                        <td className="py-2.5 pr-4 text-slate-300 text-xs">{p.credential || '-'}</td>
                        <td className="py-2.5 pr-4 text-emerald-300 font-semibold">{money(p.median_earnings)}</td>
                        {detail.programs.some((x) => x.median_debt) && (
                          <td className="py-2.5 pr-4 text-amber-300">
                            {p.median_debt ? money(p.median_debt) : <span className="text-slate-300 text-xs">Not reported</span>}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </GlassCard>
          )}

          <GlassCard className="mt-4 mb-10">
            <h3 className="font-outfit text-xl font-semibold text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-300" />What switching majors would cost
            </h3>
            <div className="grid md:grid-cols-3 gap-3 mt-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-300 font-semibold">Extra semesters</label>
                <select value={semesters} onChange={(e) => setSemesters(e.target.value)} className="input-dark w-full mt-2">
                  {[1, 2, 3, 4, 5, 6].map((n) => <option key={n} value={n}>{n} semester{n > 1 ? 's' : ''}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-300 font-semibold">Residency</label>
                <select value={residency} onChange={(e) => setResidency(e.target.value)} className="input-dark w-full mt-2">
                  <option value="in_state">In-state</option>
                  <option value="out_of_state">Out-of-state</option>
                </select>
              </div>
              <div className="flex items-end">
                <Button onClick={runImpact} className="btn-primary w-full">
                  Calculate <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
            {impact && (
              <div className="mt-5 rounded-xl border border-amber-400/30 bg-amber-400/[0.08] p-5">
                <p className="text-base text-slate-100">{impact.explanation}</p>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
                  <Stat label="Added tuition"      value={money(impact.added_tuition)}           tone="text-amber-300" />
                  <Stat label="Delayed earnings"   value={money(impact.delayed_earnings)}         tone="text-rose-300" />
                  <Stat label="True cost"          value={money(impact.total_cost_of_change)}     tone="text-white" />
                  <Stat label="Graduation delay"   value={`${impact.graduation_shift_months} mo`} />
                </div>
              </div>
            )}
          </GlassCard>
        </>
      )}
    </AppLayout>
  );
}
